/**
 * Шаблон страниц, перенесённых из WordPress (корзина «ДЕРЖИТ», 734 адреса).
 *
 * Отличие от LongFormArticleTemplate одно: тело приходит готовым HTML, а не
 * разложенным на разделы. Текст переносится ДОСЛОВНО — эти страницы приносят
 * трафик, и переписывать их не велено; меняется только обрамление.
 *
 * Тело не лежит в бандле: 734 статьи — это 12 МБ, они уехали бы в JS целиком.
 * Порядок получения тела:
 *   1) window.__ARTICLE__ — его кладёт в страницу генератор при сборке, поэтому
 *      первая отрисовка совпадает с тем, что уже отдано браузеру, и гидратация
 *      ничего не перерисовывает;
 *   2) загрузка /_articles/<slug>.json — только для переходов внутри сайта,
 *      когда страница не перезагружалась.
 *
 * Про dangerouslySetInnerHTML. Тело — не пользовательский ввод и не приходит из
 * сети во время работы: это файлы, собранные scripts/build-legacy-articles.mjs
 * и лежащие в репозитории. На сборке из них вычищается исполняемое (скрипты,
 * кроме application/ld+json, обработчики on*, адреса javascript:), и сборка
 * ОТКАЗЫВАЕТ, если после очистки что-то осталось. Санитайзер и его сторож
 * покрыты самопроверкой (`node scripts/build-legacy-articles.mjs --self-test`).
 */
import { useEffect, useState } from 'react';
import { Phone, Calendar } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';

/** То, что лежит в public/_articles/<slug>.json. */
export interface LegacyArticle {
  slug: string;
  url: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  published: string;
  modified: string;
  words: number;
  md5Text: string;
  html: string;
}

const SITE = 'https://www.morganlegalny.com';

interface Props {
  /** Полный путь адреса без ведущей и хвостовой косой: у вложенных страниц
      это «каталог/страница», а не только последний сегмент. */
  path: string;
}

declare global {
  interface Window {
    /** Кладётся генератором страниц: первая отрисовка совпадает с отданным HTML. */
    __ARTICLE__?: LegacyArticle;
  }
}

function buildSchema(a: LegacyArticle) {
  const canonical = `${SITE}${a.url}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: a.title,
      description: a.metaDescription,
      author: { '@id': `${SITE}/#organization` },
      publisher: { '@id': `${SITE}/#organization` },
      datePublished: a.published,
      dateModified: a.modified,
      mainEntityOfPage: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: a.title, item: canonical },
      ],
    },
  ];
}

function setHeadTag(selector: string, attr: 'content' | 'href', value: string) {
  const el = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (el) (el as unknown as Record<string, string>)[attr] = value;
}

function useHead(a: LegacyArticle | null | undefined) {
  useEffect(() => {
    if (typeof document === 'undefined' || !a) return;
    const canonical = `${SITE}${a.url}`;
    const title = a.metaTitle || a.title;
    document.title = title;
    setHeadTag('meta[name="description"]', 'content', a.metaDescription);
    setHeadTag('link[rel="canonical"]', 'href', canonical);
    setHeadTag('meta[property="og:title"]', 'content', title);
    setHeadTag('meta[property="og:description"]', 'content', a.metaDescription);
    setHeadTag('meta[property="og:url"]', 'content', canonical);
    setHeadTag('meta[property="og:type"]', 'content', 'article');
    setHeadTag('meta[name="twitter:title"]', 'content', title);
    setHeadTag('meta[name="twitter:description"]', 'content', a.metaDescription);
  }, [a]);
}

/** Статья: из страницы, если генератор её туда положил, иначе — загрузкой. */
function useArticle(path: string): LegacyArticle | null | undefined {
  // Файл тела назван последним сегментом (слаги проверены на единственность),
  // а сверка с вложенным в страницу — по полному адресу.
  const file = path.split('/').pop() as string;
  const url = `/${path}/`;
  const inline =
    typeof window !== 'undefined' && window.__ARTICLE__?.url === url
      ? window.__ARTICLE__
      : null;
  // undefined — ещё не знаем; null — не нашлось.
  const [article, setArticle] = useState<LegacyArticle | null | undefined>(inline ?? undefined);

  useEffect(() => {
    if (inline) { setArticle(inline); return; }
    let alive = true;
    setArticle(undefined);
    fetch(`/_articles/${file}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: LegacyArticle) => { if (alive) setArticle(d); })
      .catch(() => { if (alive) setArticle(null); });
    return () => { alive = false; };
  }, [file, inline]);

  return article;
}

export default function LegacyArticleTemplate({ path }: Props) {
  const article = useArticle(path);
  useHead(article);

  return (
    <div className="min-h-screen bg-cream">
      {article ? <SchemaJsonLd schema={buildSchema(article)} /> : null}
      <Navbar />
      <main id="legacy-article">
        <section className="relative bg-navy pt-28 lg:pt-32 pb-14 lg:pb-16">
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <span className="mx-2 text-gold/60">/</span>
              <span className="text-white/80">
                {article && article.title.length > 60 ? `${article.title.slice(0, 60)}…` : article?.title ?? ''}
              </span>
            </nav>
            <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1]">
              {article?.title ?? ''}
            </h1>
          </div>
        </section>

        <section className="py-14 lg:py-20 bg-cream">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {article ? (
              <div className="legacy-body" dangerouslySetInnerHTML={{ __html: article.html }} />
            ) : (
              <div className="legacy-body" aria-busy={article === undefined} />
            )}
          </div>
        </section>

        <section className="bg-gold py-14 lg:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight mb-5">
              Ready to Talk About Your Matter?
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/appointment/" className="btn-navy">
                <Calendar className="w-4 h-4" />
                Schedule Free Consultation
              </a>
              <a
                href="tel:8885291315"
                className="inline-flex items-center gap-2 border-2 border-navy text-navy font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-navy hover:text-gold"
              >
                <Phone className="w-4 h-4" />
                888-529-1315
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
